import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import swal from "sweetalert";
import { convertToDateString, LoadingSpinner, isUrdu } from "../../../constants/const";
import { ApiFinance } from "../ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetSelectedLanguage } from "../../helper/Method";
import { ApiFund } from "../ApiFund";
import MemberAddressPreview from "../MemberAddressPreview";

const EditUsraInfaq = ({ item, setModal, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [heads, setHeads] = useState([]);
  const [amount, setAmount] = useState(item?.amount ?? "");
  const [date, setDate] = useState(item?.date ? new Date(item.date) : new Date());
  const [account, setAccount] = useState(null);
  const [memo, setMemo] = useState(item?.memo || "");
  const [address, setAddress] = useState(item?.address || "");

  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetFinanceSubCategoryAccountsByCode("infaq")
      .then((res) => {
        const list = (res.data || []).map((h) => ({
          value: String(h.value),
          label: h.label,
        }));
        setHeads(list);
        const current = list.find(
          (h) => h.value === String(item?.infaqAccountId || ""),
        );
        setAccount(current || list[0] || null);
      })
      .catch(() => setHeads([]))
      .finally(() => setLoading(false));
  }, [item]);

  const save = () => {
    const parsedAmount = Number(amount);
    if (!parsedAmount || parsedAmount <= 0 || !date || !account) {
      swal({
        title: t("invalid_amount") || t("common_error"),
        icon: "error",
        className: GetSelectedLanguage() === "en" ? "" : "ur",
      });
      return;
    }
    setSaving(true);
    ApiFund()
      .UpdateUsraInfaq(item.id, {
        amount: parsedAmount,
        date: convertToDateString(date),
        financeAccountId: account.value,
        memo,
        address: parsedAmount >= 5000 ? address : "",
        payerName: item.payerName,
      })
      .then((res) => {
        if (res.data?.statusCode === "200") {
          setModal(false);
          if (onSuccess) onSuccess();
        } else {
          swal({
            title: t(res.data?.message || "usra_infaq_locked"),
            icon: "error",
            className: GetSelectedLanguage() === "en" ? "" : "ur",
          });
        }
      })
      .catch((err) => {
        swal({
          title: t(err.response?.data?.message || "usra_infaq_locked"),
          icon: "error",
          className: GetSelectedLanguage() === "en" ? "" : "ur",
        });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal isOpen toggle={() => setModal(false)}>
      <ModalHeader toggle={() => setModal(false)}>
        <GetLanguageString props="common_edit" />
      </ModalHeader>
      <ModalBody>
        {loading ? (
          LoadingSpinner()
        ) : (
          <>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label className="col-form-label">
                    <GetLanguageString props="funds_date" />
                  </Label>
                  <DatePicker
                    className="form-control no-drag"
                    dateFormat="dd/MM/yyyy"
                    selected={date}
                    onChange={setDate}
                    maxDate={new Date()}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="col-form-label">
                    <GetLanguageString props="funds_add_fund_amount" />
                  </Label>
                  <Input
                    className="no-drag"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label className="col-form-label">
                <GetLanguageString props="fund_account_head" />
              </Label>
              <Select
                options={heads}
                value={account}
                className="basic-single no-drag"
                classNamePrefix="select"
                onChange={setAccount}
              />
            </FormGroup>
            <FormGroup>
              <Label className="col-form-label">
                <GetLanguageString props="transactions_memo" />
              </Label>
              <Input
                type="textarea"
                className="no-drag"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </FormGroup>
            {Number(amount) >= 5000 && (
              <FormGroup>
                <Label className="col-form-label">
                  <GetLanguageString props="member_address" />
                </Label>
                <MemberAddressPreview
                  memberId={item?.memberId}
                  totalAmount={Number(amount)}
                />
                <Input
                  className={`no-drag ${isUrdu(address) ? "ur" : ""}`}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </FormGroup>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="white" onClick={() => setModal(false)}>
          <GetLanguageString props="common_cancel" />
        </Button>
        <Button color="primary" onClick={save} disabled={saving || loading}>
          <GetLanguageString props="common_save_changes" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditUsraInfaq;
